import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { Plus, Trash2, Play, RefreshCw, AlertCircle, CalendarClock, Edit2 } from 'lucide-react';
import { ModalTraspasoRecurrente } from '../components/ModalTraspasoRecurrente';
import { ModalTransaccionRecurrente } from '../components/ModalTransaccionRecurrente';
import { format, isPast, isToday } from 'date-fns';
import { es } from 'date-fns/locale';
import { useConfirmar } from '../context/ConfirmarContext';
import { useLimite } from '../hooks/useLimite';
import { ContadorLimite } from '../components/plus/ContadorLimite';
import { usePerfil } from '../context/PerfilContext';
import { usePlus } from '../context/PlusContext';
import { periodicosQueCorren } from '../config/planes';
import { proximaTrasEjecutar } from '../utils/periodicos';

const LABEL_PERIOD: Record<string, string> = {
  diario: 'Diario', laboral: 'Día laboral', mensual: 'Mensual',
  trimestral: 'Trimestral', semestral: 'Semestral', anual: 'Anual',
  semanal: 'Semanal',
};

export const TraspasosPeriodicos: React.FC = () => {
  const confirmar = useConfirmar();
  // Traspasos y transacciones periódicas comparten el mismo tope
  const limite = useLimite('periodicos');
  const {
    traspasosRecurrentes, ejecutarTraspasoRecurrente, eliminarTraspasoRecurrente,
    transaccionesRecurrentes, ejecutarTransaccionRecurrente, eliminarTransaccionRecurrente,
    cuentas, categorias, loading,
  } = useData();

  // Sin Plus, el servidor solo ejecuta los 3 activos más antiguos: el resto, en pausa
  const { esPlus } = usePerfil();
  const { ofrecerPlus } = usePlus();
  const corren = esPlus ? null : periodicosQueCorren([...traspasosRecurrentes, ...transaccionesRecurrentes]);
  const enPausa = (tr: { id: string; activo: boolean }) => !!corren && tr.activo && !corren.has(tr.id);
  const etiquetaPausa = (
    <button type="button" onClick={() => ofrecerPlus('periodicos')}
      title="Con el plan gratis solo se ejecutan los 3 más antiguos. Con Plus, todos."
      className="text-xs font-semibold bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 px-2 py-0.5 rounded-full">
      En pausa · Plus
    </button>
  );

  const [showModalTraspaso, setShowModalTraspaso]   = useState(false);
  const [showModalTx, setShowModalTx]               = useState(false);
  const [traspasoEditar, setTraspasoEditar]         = useState<typeof traspasosRecurrentes[0] | undefined>(undefined);
  const [txEditar, setTxEditar]                     = useState<typeof transaccionesRecurrentes[0] | undefined>(undefined);
  const [ejecutando, setEjecutando]                 = useState<string | null>(null);

  const getNombreCuenta   = (id: string) => cuentas.find(c => c.id === id)?.nombre || 'Desconocida';
  const getNombreCategoria = (id: string) => {
    const c = categorias.find(c => c.id === id);
    return c ? `${c.icono} ${c.nombre}` : 'Desconocida';
  };

  const esVencido = (fecha: Date) => isPast(new Date(fecha)) || isToday(new Date(fecha));

  const vencidosTraspasos = traspasosRecurrentes.filter(t => esVencido(t.proximaFecha));
  const vencidosTx        = transaccionesRecurrentes.filter(t => esVencido(t.proximaFecha));
  const totalVencidos     = vencidosTraspasos.length + vencidosTx.length;

  // Si se ejecuta antes de la fecha prevista, cuenta como esa ejecución y la
  // siguiente pasa al próximo periodo: se avisa para que no sorprenda.
  const confirmarAdelanto = async (tr: { proximaFecha: Date; periodicidad: string }) => {
    if (esVencido(tr.proximaFecha)) return true;
    const fmt = (f: Date) => format(f, "d 'de' MMMM", { locale: es });
    const siguiente = proximaTrasEjecutar(tr.proximaFecha, tr.periodicidad as any);
    return confirmar({
      titulo: '¿Adelantar la de esta vez?',
      mensaje: `Se apunta hoy y cuenta como la del ${fmt(new Date(tr.proximaFecha))}, que ya no se repetirá. La siguiente será el ${fmt(siguiente)}.`,
      accion: 'Ejecutar ahora',
    });
  };

  const handleEjecutarTraspaso = async (id: string) => {
    const tr = traspasosRecurrentes.find(t => t.id === id);
    if (!tr || !(await confirmarAdelanto(tr))) return;
    setEjecutando(id);
    try { await ejecutarTraspasoRecurrente(id); } catch (e) { console.error(e); } finally { setEjecutando(null); }
  };

  const handleEjecutarTx = async (id: string) => {
    const tr = transaccionesRecurrentes.find(t => t.id === id);
    if (!tr || !(await confirmarAdelanto(tr))) return;
    setEjecutando(id);
    try { await ejecutarTransaccionRecurrente(id); } catch (e) { console.error(e); } finally { setEjecutando(null); }
  };

  const handleEliminarTraspaso = async (id: string) => {
    if (await confirmar({ titulo: '¿Eliminar este traspaso periódico?', accion: 'Eliminar' }))
      try { await eliminarTraspasoRecurrente(id); } catch (e) { console.error(e); }
  };

  const handleEliminarTx = async (id: string) => {
    if (await confirmar({ titulo: '¿Eliminar esta transacción periódica?', accion: 'Eliminar' }))
      try { await eliminarTransaccionRecurrente(id); } catch (e) { console.error(e); }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="text-gray-600 dark:text-gray-400">Cargando…</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-8">
      <div className="max-w-5xl mx-auto space-y-10">

        {/* Alerta global de vencidos */}
        {totalVencidos > 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="font-semibold text-amber-800 dark:text-amber-300">
              {totalVencidos} elemento(s) pendiente(s) de ejecutar — pulsa ▶ en los resaltados en ámbar
            </p>
          </div>
        )}

        {/* ── TRASPASOS PERIÓDICOS ────────────────────────────────────────── */}
        <section>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <RefreshCw className="w-6 h-6 text-purple-600" /> Traspasos Periódicos
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Movimientos automáticos entre cuentas
              </p>
            </div>
            <button onClick={() => limite.intentar(() => { setTraspasoEditar(undefined); setShowModalTraspaso(true); })}
              className="flex items-center gap-2 bg-purple-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-purple-700 transition">
              <Plus className="w-5 h-5" /> Nuevo Traspaso <ContadorLimite texto={limite.texto} />
            </button>
          </div>

          {traspasosRecurrentes.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center text-gray-500 dark:text-gray-400">
              No hay traspasos periódicos configurados.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {traspasosRecurrentes.map(tr => {
                const vencido = esVencido(tr.proximaFecha);
                return (
                  <div key={tr.id}
                    className={`bg-white dark:bg-gray-800 rounded-lg shadow p-5 border-l-4 ${vencido ? 'border-amber-500' : 'border-purple-500'}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900 dark:text-white">{tr.nombre}</span>
                          <span className="text-xs font-semibold bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full">
                            {LABEL_PERIOD[tr.periodicidad]}
                          </span>
                          {enPausa(tr) && etiquetaPausa}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {getNombreCuenta(tr.cuentaOrigenId)} → {tr.inversionDestinoId ? '📈 Inversión' : getNombreCuenta(tr.cuentaDestinoId ?? '')}
                        </p>
                        {tr.categoriaOrigenId && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            {getNombreCategoria(tr.categoriaOrigenId)}
                          </p>
                        )}
                      </div>
                      <span className="text-lg font-bold text-purple-600 dark:text-purple-400 shrink-0 ml-2">
                        €{tr.cantidad.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-xs font-semibold ${vencido ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
                          {vencido ? '⚠ Pendiente: ' : 'Próxima: '}
                          {format(new Date(tr.proximaFecha), 'dd MMM yyyy', { locale: es })}
                        </p>
                        {tr.fechaFin && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            Fin: {format(new Date(tr.fechaFin), 'dd MMM yyyy', { locale: es })}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleEjecutarTraspaso(tr.id)} disabled={ejecutando === tr.id}
                          className={`flex items-center gap-1 text-sm font-semibold px-3 py-1.5 rounded-lg transition disabled:opacity-50 ${vencido ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-purple-600 hover:bg-purple-700 text-white'}`}>
                          <Play className="w-4 h-4" />
                          {ejecutando === tr.id ? '…' : 'Ejecutar'}
                        </button>
                        <button onClick={() => { setTraspasoEditar(tr); setShowModalTraspaso(true); }}
                          className="text-blue-600 hover:text-blue-800 transition p-1.5" title="Editar">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleEliminarTraspaso(tr.id)}
                          className="text-red-600 hover:text-red-800 transition p-1.5">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ── TRANSACCIONES PERIÓDICAS ─────────────────────────────────────── */}
        <section>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <CalendarClock className="w-6 h-6 text-blue-600" /> Transacciones Periódicas
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Ingresos o gastos que se repiten en el tiempo
              </p>
            </div>
            <button onClick={() => limite.intentar(() => { setTxEditar(undefined); setShowModalTx(true); })}
              className="flex items-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 transition">
              <Plus className="w-5 h-5" /> Nueva Transacción <ContadorLimite texto={limite.texto} />
            </button>
          </div>

          {transaccionesRecurrentes.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center text-gray-500 dark:text-gray-400">
              No hay transacciones periódicas configuradas.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {transaccionesRecurrentes.map(tr => {
                const vencido = esVencido(tr.proximaFecha);
                const esIngreso = tr.tipo === 'ingreso';
                return (
                  <div key={tr.id}
                    className={`bg-white dark:bg-gray-800 rounded-lg shadow p-5 border-l-4 ${vencido ? 'border-amber-500' : esIngreso ? 'border-green-500' : 'border-red-500'}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900 dark:text-white">{tr.nombre}</span>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${esIngreso ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300'}`}>
                            {esIngreso ? 'Ingreso' : 'Gasto'}
                          </span>
                          <span className="text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full">
                            {LABEL_PERIOD[tr.periodicidad]}
                          </span>
                          {enPausa(tr) && etiquetaPausa}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {getNombreCategoria(tr.categoriaId)} · {getNombreCuenta(tr.cuentaId)}
                        </p>
                      </div>
                      <span className={`text-lg font-bold shrink-0 ml-2 ${esIngreso ? 'text-green-600' : 'text-red-600'}`}>
                        {esIngreso ? '+' : '-'}€{tr.cantidad.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-xs font-semibold ${vencido ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
                          {vencido ? '⚠ Pendiente: ' : 'Próxima: '}
                          {format(new Date(tr.proximaFecha), 'dd MMM yyyy', { locale: es })}
                        </p>
                        {tr.fechaFin && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            Fin: {format(new Date(tr.fechaFin), 'dd MMM yyyy', { locale: es })}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleEjecutarTx(tr.id)} disabled={ejecutando === tr.id}
                          className={`flex items-center gap-1 text-sm font-semibold px-3 py-1.5 rounded-lg transition disabled:opacity-50 ${vencido ? 'bg-amber-500 hover:bg-amber-600 text-white' : esIngreso ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>
                          <Play className="w-4 h-4" />
                          {ejecutando === tr.id ? '…' : 'Ejecutar'}
                        </button>
                        <button onClick={() => { setTxEditar(tr); setShowModalTx(true); }}
                          className="text-blue-600 hover:text-blue-800 transition p-1.5" title="Editar">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleEliminarTx(tr.id)}
                          className="text-red-600 hover:text-red-800 transition p-1.5">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

      </div>

      {showModalTraspaso && (
        <ModalTraspasoRecurrente
          onClose={() => { setShowModalTraspaso(false); setTraspasoEditar(undefined); }}
          traspasoEditar={traspasoEditar}
        />
      )}
      {showModalTx && (
        <ModalTransaccionRecurrente
          onClose={() => { setShowModalTx(false); setTxEditar(undefined); }}
          transaccionEditar={txEditar}
        />
      )}
    </div>
  );
};
